import { Box, Circle, ProgressBar, Text, colors } from '@factorialco/deprecated-design-system'
import { CardDetailFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { SidebarSection } from 'modules/banking/components/CardSidepanel/_Content/_SidebarSection'
import { isSingleUse } from 'modules/banking/lib/cards/isSingleUse'

const AmountLeft: React.FC<{ amountLeft: string }> = ({ amountLeft }) => {
  return (
    <Box flexDirection="row" gap="s4">
      <Box alignSelf="center">
        <Circle color={colors.viridian100} type="primary" size={12} />
      </Box>
      <Text.Normal weight="normal" size="300" color="textSecondary">
        {i18n.t('banking.cards.card_controls.amount_available', {
          amount: amountLeft,
        })}
      </Text.Normal>
    </Box>
  )
}

const AmountSpent: React.FC<{ amountSpent: string }> = ({ amountSpent }) => {
  return (
    <Box flexDirection="row" gap="s4">
      <Box alignSelf="center">
        <Circle type="viridian" size={10} />
      </Box>
      <Text.Normal weight="normal" size="300" color="textSecondary">
        {i18n.t('banking.cards.card_controls.amount_spent', {
          amount: amountSpent,
        })}
      </Text.Normal>
    </Box>
  )
}

const Budget: React.FC<{
  cardLimitWithCurrency: string
  period: string
  amountSpent: number
  cardLimit: number
}> = ({ cardLimitWithCurrency, period, amountSpent, cardLimit }) => {
  return (
    <>
      <Box width="full">
        <Text.Normal weight="normal" size="300" color="textSecondary">
          {i18n.t('banking.cards.card_controls.budget_amount_with_period', {
            amount: cardLimitWithCurrency,
            period,
          })}
        </Text.Normal>
      </Box>
      <Box width="full">
        <ProgressBar
          showLabel={false}
          progressColor="viridian1000"
          amount={amountSpent}
          total={cardLimit}
        />
      </Box>
    </>
  )
}

export const Limits: React.FC<{ card: CardDetailFragment }> = ({ card }) => {
  const {
    access: { locale: accessLocale },
  } = useCurrentSession()
  const currency = card.cardLimit.currency
  const amountLeft = card.cardLimit.remainingForCurrentPeriod
  const amountSpent = card.cardLimit.spentDuringCurrentPeriod || 0
  const cardLimit = card.cardLimit.limitForCurrentPeriod
  const period = card.cardLimit.period
  const amountSpentWithCurrency = MoneyWithCurrency({
    amount: amountSpent,
    currency,
    locale: accessLocale,
  })
  const amountLeftWithCurrency = amountLeft
    ? MoneyWithCurrency({
        amount: amountLeft,
        currency,
        locale: accessLocale,
      })
    : ''
  const cardLimitWithCurrency = cardLimit
    ? MoneyWithCurrency({
        amount: cardLimit,
        currency,
        locale: accessLocale,
      })
    : ''
  let periodText = ''
  switch (period) {
    case 'daily':
      periodText = i18n.t('expenses_v2.cards.limit.intervals.daily')
      break
    case 'monthly':
      periodText = i18n.t('expenses_v2.cards.limit.intervals.monthly')
      break
    case 'weekly':
      periodText = i18n.t('expenses_v2.cards.limit.intervals.weekly')
      break
    case 'yearly':
      periodText = i18n.t('expenses_v2.cards.limit.intervals.yearly')
      break
  }
  if (isSingleUse(card)) {
    periodText = ' '
  }

  return (
    <SidebarSection>
      <Box width="full">
        <Text.Normal weight="semibold" size="300" color="textPrimary">
          {i18n.t('banking.cards.card_controls.budget')}
        </Text.Normal>
      </Box>
      {cardLimit && (
        <Budget
          cardLimitWithCurrency={cardLimitWithCurrency}
          period={periodText}
          amountSpent={amountSpent}
          cardLimit={cardLimit}
        />
      )}
      <Box width="full" flexDirection="row" justifyContent="spaceBetween">
        {amountSpentWithCurrency && <AmountSpent amountSpent={amountSpentWithCurrency} />}
        {amountLeft && <AmountLeft amountLeft={amountLeftWithCurrency} />}
      </Box>
    </SidebarSection>
  )
}
